// Calendar (design "Calendar" screen): month grid of the organizer's
// schedule-lite meetings (F-PLAT-7) + a today agenda. The time gates nothing —
// every event is just a link into its meeting.

import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { api, Me, MeetingCreated, MeetingSummary } from '../api';
import { ScheduleDrawer } from '../components/ScheduleDrawer';
import { TopBar } from '../components/TopBar';
import { Button } from '../ui/Button';
import { IconButton } from '../ui/IconButton';
import { IconChevronLeft, IconChevronRight } from '../ui/icons';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface Cell {
  day: number | null;
  date: Date | null;
  events: MeetingSummary[];
}

function buildCells(year: number, month: number, meetings: MeetingSummary[]): Cell[] {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // Monday-start
  const byDay = new Map<number, MeetingSummary[]>();
  for (const m of meetings) {
    if (!m.scheduledAt) continue;
    const d = new Date(m.scheduledAt);
    if (d.getFullYear() !== year || d.getMonth() !== month) continue;
    const list = byDay.get(d.getDate()) ?? [];
    list.push(m);
    byDay.set(d.getDate(), list);
  }
  const cells: Cell[] = [];
  for (let i = 0; i < lead; i++) cells.push({ day: null, date: null, events: [] });
  for (let d = 1; d <= daysInMonth; d++) {
    const events = (byDay.get(d) ?? []).sort(
      (a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime(),
    );
    cells.push({ day: d, date: new Date(year, month, d), events });
  }
  while (cells.length % 7 !== 0 || cells.length < 35)
    cells.push({ day: null, date: null, events: [] });
  return cells;
}

function shortTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function CalendarPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [meetings, setMeetings] = useState<MeetingSummary[]>([]);
  const [ym, setYm] = useState(() => {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() };
  });
  const year = ym.y;
  const month = ym.m;
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch(() => void navigate('/'));
  }, [navigate]);

  useEffect(() => {
    api
      .listMeetings()
      .then(setMeetings)
      .catch(() => setMeetings([]));
  }, [refresh]);

  const cells = useMemo(() => buildCells(year, month, meetings), [year, month, meetings]);
  const today = new Date();
  const todayAgenda = useMemo(
    () =>
      meetings
        .filter(
          (m) => m.scheduledAt && new Date(m.scheduledAt).toDateString() === today.toDateString(),
        )
        .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meetings],
  );

  const prevMonth = useCallback(() => {
    setYm(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }));
  }, []);
  const nextMonth = useCallback(() => {
    setYm(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }));
  }, []);

  const goBack = useCallback(() => void navigate('/'), [navigate]);
  const openSchedule = useCallback(() => setScheduleOpen(true), []);
  const closeSchedule = useCallback(() => setScheduleOpen(false), []);
  const onScheduled = useCallback((_m: MeetingCreated) => setRefresh((n) => n + 1), []);
  const onSignedOut = useCallback(() => void navigate('/'), [navigate]);
  const openEvent = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const slug = e.currentTarget.dataset.slug;
      if (slug) void navigate(`/m/${slug}`);
    },
    [navigate],
  );

  if (!me) return <div className="bz-state-page">Loading…</div>;

  const monthLabel = new Date(year, month, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
  const todayLabel = today.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="bz-cal">
      <TopBar me={me} section="Calendar" onSignedOut={onSignedOut} />
      <div className="bz-cal-main">
        <div className="bz-cal-month">
          <div className="bz-cal-monthhead">
            <h2>{monthLabel}</h2>
            <div className="bz-cal-nav">
              <IconButton shape="flat" label="Previous month" onClick={prevMonth}>
                <IconChevronLeft size={13} />
              </IconButton>
              <IconButton shape="flat" label="Next month" onClick={nextMonth}>
                <IconChevronRight size={13} />
              </IconButton>
            </div>
            <div className="bz-cal-headactions">
              <Button size="sm" onClick={goBack}>
                Back to home
              </Button>
              <Button size="sm" variant="primary" onClick={openSchedule}>
                Schedule meeting
              </Button>
            </div>
          </div>
          <div className="bz-cal-weekdays">
            {WEEKDAYS.map((wd) => (
              <span key={wd} className="bz-cal-weekday">
                {wd}
              </span>
            ))}
          </div>
          <div className="bz-cal-grid">
            {cells.map((c, i) => {
              const isToday = c.date?.toDateString() === today.toDateString();
              const cls = c.day
                ? isToday
                  ? 'bz-cal-cell bz-cal-cell-today'
                  : 'bz-cal-cell'
                : 'bz-cal-cell bz-cal-cell-empty';
              return (
                <div key={i} className={cls}>
                  {c.day && <span className="bz-cal-day">{c.day}</span>}
                  {c.events.map((ev) => (
                    <button
                      key={ev.slug}
                      type="button"
                      className={
                        ev.e2eeModel === 'b' ? 'bz-cal-event bz-cal-event-zk' : 'bz-cal-event'
                      }
                      data-slug={ev.slug}
                      onClick={openEvent}
                    >
                      {shortTime(ev.scheduledAt!)} {ev.title ?? ev.slug}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
        <div className="bz-cal-side">
          <div className="bz-cal-side-head">
            <span>Today</span>
            <span>{todayLabel}</span>
          </div>
          {todayAgenda.length === 0 && <p className="bz-empty">Nothing scheduled today.</p>}
          {todayAgenda.map((m) => (
            <div key={m.slug} className="bz-row-card">
              <div className="bz-agenda-bar" />
              <div className="bz-rowmain">
                <div className="bz-meeting-rowtitle">{m.title ?? m.slug}</div>
                <div className="bz-meeting-rowmeta">
                  {shortTime(m.scheduledAt!)}
                  {m.e2eeModel === 'b' ? ' · zero-knowledge' : ''}
                </div>
              </div>
              <Button size="sm" data-slug={m.slug} onClick={openEvent}>
                Join
              </Button>
            </div>
          ))}
        </div>
      </div>
      {scheduleOpen && <ScheduleDrawer onClose={closeSchedule} onCreated={onScheduled} />}
    </div>
  );
}
